import type { RaceClass } from "./balance";

export type RacePhase = "menu" | "countdown" | "race" | "results" | "finished";
export type KartController = "human" | "ai" | "twitch";
export type { RaceClass } from "./balance";
export type TwitchDirective = "steady" | "attack" | "defend" | "boost";
export type CourseEventPhase = "telegraph" | "active" | "cooldown";
export type CourseEventLane = -1 | 1;
export type CourseEventPatternIndex = 0 | 1;
export type ItemKind = "turbo" | "six-pack" | "shield" | "keg" | "foam" | "last-orders" | "bottle" | "hiccups" | "lime-wedge" | "spill" | "goggles" | "round" | "corkscrew";
export type KartSurface = "road" | "shortcut" | "offroad";
export type KartWeightClass = "light" | "medium" | "heavy";
export type KartPattern = "solid" | "stripe" | "rally" | "split";
export type KartTopper = "none" | "cap" | "lime" | "crown" | "laurel";
export type KartSticker = "none" | "hop" | "bolt" | "pint" | "medal";
export type KartTrail = "bubbles" | "flame" | "foam" | "hops" | "neon" | "stars" | "none";
export type KartHorn = "toot" | "lager" | "air" | "bell";
export type VenueChallengeMedal = "none" | "bronze" | "silver" | "gold";
export type VenueChallengeStatus = "active" | "passed" | "failed";

export interface RaceVariantState {
  seed: number;
  index: 0 | 1 | 2;
  id: "classic" | "switchback" | "staggered";
  itemLayout: "full-grid" | "split-rows" | "offset-rows";
  hazardLayout: "paired" | "switchback" | "slalom";
}

export interface DriverInput {
  throttle: number;
  brake: number;
  steer: number;
  drift: boolean;
  useSeq?: number;
}

export interface ItemBoxState {
  id: string;
  distance: number;
  lateral: number;
  x: number;
  y: number;
  z: number;
  respawnAt: number;
}

export interface ProjectileState {
  id: number;
  kind: "keg" | "bottle";
  ownerId: string;
  targetId: string | null;
  x: number;
  y: number;
  z: number;
  velocityX: number;
  velocityZ: number;
  expiresAt: number;
  /** Distance travelled by fast, bounded projectiles such as Bottle. */
  distanceTravelled?: number;
  /** Maximum travel distance in world metres before the projectile expires. */
  maxDistance?: number;
  /** Number of rail banks already made by a straight-fired Bottle. */
  bounceCount?: number;
  /** Hard cap which keeps bank shots readable and finite. */
  maxBounces?: number;
  /** Last projected route distance, used to keep bank shots on the correct crossover deck. */
  previousDistance?: number;
}

export interface HazardState {
  id: string;
  kind: "foam" | "spill";
  /** Present for temporary player-dropped foam; fixed course hazards use null/undefined. */
  ownerId?: string | null;
  x: number;
  y: number;
  z: number;
  radius: number;
  distance: number;
  lateral: number;
  respawnAt: number;
  expiresAt?: number;
}

/**
 * Server-authoritative state for the one dynamic set piece assigned to a
 * course.  `distance` is the event centre in the track's local distance
 * space; `lap` makes the same centre unambiguous once the field has completed
 * a lap.  `phaseTime` is elapsed time in the current phase, while
 * `phaseRemaining` is the corresponding countdown for UI/presentation.
 */
export interface CourseEventState {
  id: string;
  definitionId: string;
  patternIndex: CourseEventPatternIndex;
  label: string;
  name: string;
  trackId: string;
  phase: CourseEventPhase;
  lap: number;
  triggerCount: number;
  blockedLane: CourseEventLane;
  safeLane: CourseEventLane;
  /** Backwards-friendly alias for the blocked lane used by lightweight clients. */
  lane: CourseEventLane;
  phaseTime: number;
  phaseRemaining: number;
  hitCount: number;
  distance: number;
  eventDistance: number;
  triggerDistance: number;
  phaseStartedAt: number;
  phaseEndsAt: number;
}

export interface KartState {
  id: string;
  /** Server-authored AI personality identity; absent for legacy snapshots and human-only slots. */
  driverProfileId?: string;
  /**
   * Optional presentation-only Twitch login. The authoritative server sets
   * this only for a real Twitch controller or a verified browser account;
   * display names and client hello claims must never populate it.
   */
  avatarLogin?: string;
  name: string;
  color: number;
  accentColor: number;
  variant: number;
  livery: number;
  wheelStyle: number;
  driverColor: number;
  weightClass: KartWeightClass;
  pattern: KartPattern;
  topper: KartTopper;
  sticker: KartSticker;
  underglow: number;
  horn: KartHorn;
  skidColor: number;
  trail: KartTrail;
  raceNumber: string;
  controller: KartController;
  /** Server-authoritative manual-control handoff. Only a human kart may use it. */
  autopilot?: boolean;
  twitchLogin: string | null;
  twitchDirective: TwitchDirective;
  directiveUntil: number;
  distance: number;
  lateral: number;
  x: number;
  y: number;
  z: number;
  velocityX: number;
  velocityZ: number;
  yaw: number;
  speed: number;
  launchCharge: number;
  driftCharge: number;
  driftDirection: -1 | 0 | 1;
  boostTime: number;
  /** Brief authoritative projectile-evade window from a mini-turbo or boost item. */
  itemDodgeTime: number;
  /** Generic authoritative immunity window for item effects. */
  itemImmunityTime: number;
  /** Temporary Beer Goggles phase window; phasing karts ignore contact, hazards, and projectiles. */
  ghostTime: number;
  draftCharge: number;
  draftBoostTime: number;
  draftCooldown: number;
  draftTargetId: string | null;
  draftBoostCount: number;
  heldItem: ItemKind | null;
  shieldTime: number;
  /** Temporary Hiccups speed/steering debuff. */
  hiccupTime: number;
  spinTime: number;
  /** Brief shared steering/heading stabilization after a hit releases control. */
  spinRecoveryTime: number;
  itemCooldown: number;
  itemHeldAt: number;
  lastUseSeq: number;
  itemRolls: number;
  itemHitCount: number;
  /** Most recent offensive item credited by itemHitCount. */
  lastItemHit: ItemKind | null;
  /** Number of held items removed by an opponent's Corkscrew. */
  itemStealCount: number;
  /** Most recent item removed by Corkscrew, for presentation feedback. */
  lastItemStolen: ItemKind | null;
  itemGuardCount: number;
  kegDodgeCount: number;
  /** Most recent projectile avoided by the dodge counter. */
  lastItemDodge: ItemKind | null;
  pendingItemUse: boolean;
  laneBias: number;
  laneBiasUntil: number;
  hornSeq: number;
  verticalVelocity: number;
  airborne: boolean;
  airTime: number;
  trickQueued: boolean;
  /** Source sample marker currently latched by a source-indexed jump pass. */
  classicJumpedSampleIndex: number | null;
  /** Source-local cursor; checkpoint fields below remain classic-only. */
  classicSampleIndex?: number;
  classicNextCheckpoint?: number;
  classicGatesPassed?: number;
  classicProgress?: number;
  rampCooldown: number;
  hazardCooldown: number;
  courseEventCooldown: number;
  /** Number of dynamic course-event impacts suffered in this race. */
  courseEventHitCount: number;
  onShortcut: boolean;
  surface: KartSurface;
  offRoadTime: number;
  wrongWayTime: number;
  recoveryTime: number;
  recoveryCount: number;
  signatureZoneId: string | null;
  signatureZoneTime: number;
  signatureZoneTriggerCount: number;
  signatureZoneLastLap: number;
  safeDistance: number;
  lap: number;
  place: number;
  finishedAt: number | null;
}

export interface RaceState {
  trackId: string;
  raceVariant: RaceVariantState;
  raceClass: RaceClass;
  phase: RacePhase;
  elapsed: number;
  countdown: number;
  totalLaps: number;
  trackLength: number;
  karts: KartState[];
  itemBoxes: ItemBoxState[];
  projectiles: ProjectileState[];
  hazards: HazardState[];
  nextProjectileId: number;
  courseEvent: CourseEventState | null;
  cup?: CupState;
}

export interface TimeTrialTiming {
  lapStartedAt: number;
  currentLapTime: number;
  lastLapTime: number | null;
  bestLapTime: number | null;
  completedLaps: number[];
}

export interface TimeTrialState extends RaceState {
  readonly mode: "time-trial";
  timeTrial: TimeTrialTiming;
}

/** A single full-grid race that deliberately does not participate in a cup. */
export interface ExhibitionState extends RaceState {
  readonly mode: "exhibition";
}

export interface VenueChallengeRuleProgress {
  id: string;
  label: string;
  progress: number;
  target: number;
  failed: boolean;
}

export interface VenueChallengeProgress {
  definitionId: string;
  status: VenueChallengeStatus;
  medal: VenueChallengeMedal;
  rules: VenueChallengeRuleProgress[];
  peakSpeed: number;
  clean: boolean;
  miniTurbos: number;
  tricks: number;
  shortcutEntries: number;
  spinHits: number;
  observedSignatureTriggers: number;
  observedDraftBoosts: number;
  observedItemRolls: number;
  completedAt: number | null;
}

export interface VenueChallengeState extends RaceState {
  readonly mode: "venue-challenge";
  venueChallenge: VenueChallengeProgress;
}

export interface CupState {
  raceIndex: number;
  totalRaces: number;
  pointsByKart: Record<string, number>;
  completed: boolean;
  /** Opt-in runtime cup whose entrants may rotate between rounds. */
  openRoster?: boolean;
  /** Stable display names for open-roster entrants that are off the grid. */
  nameByKart?: Record<string, string>;
  /** Extended fields are optional so existing snapshots and QA fixtures remain valid. */
  raceClass?: RaceClass;
  trackOrder?: string[];
  entrantOrder?: string[];
  winsByKart?: Record<string, number>;
  finishCountsByKart?: Record<string, number[]>;
  awardedRaceKeys?: Record<string, true>;
  resultsByTrack?: Record<string, CupRaceRecord>;
  championKartId?: string | null;
  gradeByKart?: Record<string, CupGrade>;
  variantSeed?: number;
  routeId?: ChampionshipRouteId;
  duelRivalKartId?: string | null;
}

export type CupGrade = "none" | "bronze" | "silver" | "gold" | "three-star";
export type ChampionshipRouteId = "brewers-tour" | "last-orders-tour";

export interface CupRaceRecord {
  trackId: string;
  resultId: string;
  resultKey: string;
  orderedKartIds: string[];
  pointsByKart: Record<string, number>;
}

export interface CareerClassProgress {
  bestGrade: CupGrade;
  bestPoints: number;
  bestFinish: number | null;
  bestWins: number;
  completions: number;
  recordedCups: Record<string, true>;
}

export interface CareerProfile {
  version: 1;
  classes: Record<RaceClass, CareerClassProgress>;
}

export const NEUTRAL_INPUT: DriverInput = {
  throttle: 0,
  brake: 0,
  steer: 0,
  drift: false,
  useSeq: 0,
};
